import React, { useCallback, useMemo, useState } from "react";

import usePostPrint from "../../apis/usePostPrint/usePostPrint";
import { displayCentsWithDollarSign } from "../../common/helpers/stringUtils";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { addDatadogAction, addDatadogError, buildLocationContext } from "../../services/datadog";
import { IPrintHistoryEntry, getPrintHistory } from "../../services/printHistory";
import "./ReprintOrdersModal.scss";

interface IReprintOrdersModalProps {
	open: boolean;
	onClose: () => void;
}

const formatPrintTime = (timestamp: number): string =>
	new Date(timestamp).toLocaleString("en-SG", { timeZone: "Asia/Singapore" });

const ReprintOrdersModal: React.FC<IReprintOrdersModalProps> = ({ open, onClose }) => {
	const { state: location } = useConfigContext((s) => s.location);
	const { state: stall } = useConfigContext((s) => s.config.stall);
	const { submitPrint } = usePostPrint();
	const [reprintingId, setReprintingId] = useState<string | null>(null);

	// Snapshot the history when the modal opens so the list stays stable
	// while a staff member is reading it.
	const entries = useMemo<IPrintHistoryEntry[]>(() => (open ? getPrintHistory() : []), [open]);

	const handleReprint = useCallback(
		async (entry: IPrintHistoryEntry) => {
			if (reprintingId) return;

			addDatadogAction("reprint_order_clicked", {
				...buildLocationContext(location, stall),
				type: entry.type,
				amountCents: entry.amountCents,
			});
			setReprintingId(entry.id);

			try {
				await submitPrint(entry.payload);
				addDatadogAction("reprint_order_success", {
					...buildLocationContext(location, stall),
					type: entry.type,
				});
			} catch (err) {
				addDatadogError(err instanceof Error ? err : new Error("Reprint failed"), {
					...buildLocationContext(location, stall),
					type: entry.type,
				});
			} finally {
				setReprintingId(null);
			}
		},
		[reprintingId, submitPrint, location, stall]
	);

	if (!open) return null;

	return (
		<div
			className="ReprintOrdersModal"
			data-testid="reprint-orders-dialog"
			role="dialog"
			aria-modal="true"
		>
			<h1 className="ReprintOrdersModal__title">RE-PRINT</h1>

			<div className="ReprintOrdersModal__body">
				{entries.length === 0 ? (
					<p className="ReprintOrdersModal__empty" data-testid="reprint-empty">
						No recent prints available.
					</p>
				) : (
					<table className="ReprintOrdersModal__table">
						<thead>
							<tr>
								<th>Time</th>
								<th>Type</th>
								<th>Amount</th>
								<th aria-label="Reprint" />
							</tr>
						</thead>
						<tbody>
							{entries.map((entry) => (
								<tr key={entry.id} data-testid="reprint-row">
									<td>{formatPrintTime(entry.timestamp)}</td>
									<td>{entry.type === "cashQR" ? "Cash" : "Order"}</td>
									<td>{displayCentsWithDollarSign(entry.amountCents)}</td>
									<td>
										<button
											className="ReprintOrdersModal__print-btn"
											data-testid={`reprint-print-${entry.id}`}
											onClick={() => handleReprint(entry)}
											disabled={reprintingId !== null}
											type="button"
										>
											{reprintingId === entry.id ? "Printing..." : "Print"}
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			<button
				className="ReprintOrdersModal__close"
				data-testid="reprint-close"
				onClick={onClose}
				type="button"
			>
				Close
			</button>
		</div>
	);
};

export default ReprintOrdersModal;
